import { z } from 'zod';
import { METRIC_UNITS } from './metrics.js';

/**
 * Widgets are declared by plugins as *data contracts*, not React components, so
 * the same declaration renders in the web portal, the desktop app and the mobile
 * app. A plugin only ships a custom component when the built-in types can't
 * express it.
 */
export const WIDGET_TYPES = [
  'stat',       // single big number + delta + sparkline
  'gauge',      // radial 0..100
  'timeseries', // line / area chart
  'bar',        // categorical bars
  'table',      // rows of records
  'status-grid',// dense health tiles, one per resource
  'list',       // ordered list of items (top talkers, recent calls)
  'map',        // geo markers for sites/lines
  'heatmap',    // 2D intensity (hour x day)
  'custom',     // plugin-provided component, web only
] as const;
export type WidgetType = (typeof WIDGET_TYPES)[number];

export const widgetDefinitionSchema = z.object({
  /** Unique within the plugin. Full id becomes `${pluginId}:${id}`. */
  id: z.string(),
  title: z.string(),
  description: z.string().default(''),
  type: z.enum(WIDGET_TYPES),
  /** Default grid footprint, in 12-column units and 40px rows. */
  defaultSize: z.object({ w: z.number().int().min(1).max(12), h: z.number().int().min(1) }),
  minSize: z.object({ w: z.number().int().min(1), h: z.number().int().min(1) }).optional(),
  /** Server endpoint the widget polls/subscribes to, relative to the plugin. */
  dataEndpoint: z.string(),
  /** Seconds between refreshes. 0 = live via websocket only. */
  refreshSec: z.number().int().min(0).default(30),
  /** JSON-schema-ish description of user-editable widget options. */
  options: z
    .array(
      z.object({
        key: z.string(),
        label: z.string(),
        type: z.enum(['string', 'number', 'boolean', 'select', 'resource', 'metric', 'multiselect']),
        default: z.unknown().optional(),
        choices: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
        /** For `resource` options: restrict the picker to these kinds. */
        resourceKinds: z.array(z.string()).optional(),
      }),
    )
    .default([]),
  /** Roles allowed to see this widget. Empty = everyone. */
  requiredRoles: z.array(z.string()).default([]),
});
export type WidgetDefinition = z.infer<typeof widgetDefinitionSchema>;

/**
 * Visual intent for a mark. Named so plugin authors can annotate a computed
 * value — a bare ternary widens to `string`, and `as const` is not valid on an
 * expression, which is a very easy trap to fall into here.
 */
export type Intent = 'neutral' | 'good' | 'warn' | 'bad';

/** Shapes returned by `dataEndpoint`, discriminated by widget type. */
export interface StatWidgetData {
  value: number | string;
  unit?: (typeof METRIC_UNITS)[number];
  label?: string;
  /** Percentage change vs the comparison window. */
  deltaPct?: number | null;
  /** Optional inline sparkline. */
  spark?: Array<[number, number | null]>;
  intent?: 'neutral' | 'good' | 'warn' | 'bad';
  footnote?: string;
}

export interface GaugeWidgetData {
  value: number;
  min?: number;
  max?: number;
  unit?: (typeof METRIC_UNITS)[number];
  thresholds?: Array<{ at: number; intent: 'good' | 'warn' | 'bad' }>;
  label?: string;
}

export interface TimeseriesWidgetData {
  unit?: (typeof METRIC_UNITS)[number];
  stacked?: boolean;
  series: Array<{
    name: string;
    points: Array<[number, number | null]>;
    intent?: 'neutral' | 'good' | 'warn' | 'bad';
  }>;
}

export interface BarWidgetData {
  unit?: (typeof METRIC_UNITS)[number];
  horizontal?: boolean;
  bars: Array<{ label: string; value: number; intent?: 'neutral' | 'good' | 'warn' | 'bad' }>;
}

export interface TableWidgetData {
  columns: Array<{
    key: string;
    label: string;
    align?: 'left' | 'right' | 'center';
    format?: 'text' | 'number' | 'bytes' | 'duration' | 'datetime' | 'health' | 'currency' | 'badge';
    width?: number;
  }>;
  rows: Array<Record<string, unknown>>;
  /** Row click navigates here, with `:key` placeholders filled from the row. */
  rowLink?: string;
}

export interface StatusGridWidgetData {
  tiles: Array<{
    id: string;
    label: string;
    health: string;
    sublabel?: string;
    link?: string;
    /** Owning client and site (branch) names — filled in by the server for any tile that is a resource. */
    client?: string;
    site?: string;
  }>;
}

export interface ListWidgetData {
  items: Array<{
    id: string;
    primary: string;
    secondary?: string;
    trailing?: string;
    intent?: 'neutral' | 'good' | 'warn' | 'bad';
    link?: string;
  }>;
}

export interface MapWidgetData {
  markers: Array<{
    id: string;
    lat: number;
    lon: number;
    label: string;
    health: string;
    link?: string;
  }>;
}

export interface HeatmapWidgetData {
  xLabels: string[];
  yLabels: string[];
  /** cells[y][x] */
  cells: Array<Array<number | null>>;
  unit?: (typeof METRIC_UNITS)[number];
}

export type WidgetData =
  | StatWidgetData
  | GaugeWidgetData
  | TimeseriesWidgetData
  | BarWidgetData
  | TableWidgetData
  | StatusGridWidgetData
  | ListWidgetData
  | MapWidgetData
  | HeatmapWidgetData;

/** A widget as placed on a user's dashboard. */
export const dashboardWidgetSchema = z.object({
  instanceId: z.string(),
  /** `${pluginId}:${widgetId}` */
  widgetId: z.string(),
  title: z.string().nullable().default(null),
  layout: z.object({
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    w: z.number().int().min(1).max(12),
    h: z.number().int().min(1),
  }),
  options: z.record(z.unknown()).default({}),
});
export type DashboardWidget = z.infer<typeof dashboardWidgetSchema>;

export const dashboardSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** null = shared/global dashboard, otherwise owned by this user. */
  ownerId: z.string().nullable().default(null),
  isDefault: z.boolean().default(false),
  /** Scope every widget on this dashboard to one customer. */
  customerId: z.string().nullable().default(null),
  widgets: z.array(dashboardWidgetSchema).default([]),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Dashboard = z.infer<typeof dashboardSchema>;
